import { useState, useEffect, useMemo, useCallback } from 'react';
import { useMemories } from '../../hooks/useApi';
import { apiClient } from '../../services/apiClient';
import MarkdownRenderer from '../common/MarkdownRenderer/MarkdownRenderer';
import PageHeader from '../common/PageHeader/PageHeader';
import { toastService } from '../../services/toastService';
import PortalModal from '../common/PortalModal/PortalModal';
import Table from '../common/Table/Table';
import Button from '../common/Button/Button';
import SearchBar from '../common/SearchBar/SearchBar';
import Select from '../common/Select/Select';
import './MemoryManagement.css';

// Helper functions to extract supervisor and extended models from memory
const extractLLMModel = (memory) => {
  if (!memory) return 'Not assigned';

  // First, try to get from metadata (most reliable)
  if (memory.metadata?.llm_model) {
    return memory.metadata.llm_model;
  }

  if (memory.metadata?.llmModel) {
    return memory.metadata.llmModel;
  }

  // Try to extract from agentsUsed if it contains "Supervisor-"
  if (memory.agentsUsed && Array.isArray(memory.agentsUsed)) {
    const supervisorAgent = memory.agentsUsed.find(agent =>
      typeof agent === 'string' && agent.startsWith('Supervisor-')
    );
    if (supervisorAgent) {
      // Extract provider from "Supervisor-OPENAI" -> "OPENAI"
      const provider = supervisorAgent.replace('Supervisor-', '');
      // Try to get model from metadata with this provider
      if (memory.metadata?.llm_provider === provider.toLowerCase() && memory.metadata?.llm_model) {
        return memory.metadata.llm_model;
      }
      // Fallback to provider name
      return provider;
    }
  }

  // Check llmResponses for supervisor info
  if (memory.llmResponses && Array.isArray(memory.llmResponses)) {
    const supervisorResponse = memory.llmResponses.find(resp =>
      resp.agent && typeof resp.agent === 'string' && resp.agent.startsWith('Supervisor-')
    );
    if (supervisorResponse) {
      const provider = supervisorResponse.agent.replace('Supervisor-', '');
      return provider;
    }
  }

  // Legacy support: check for old format
  if (memory.metadata?.supervisor_info?.model_info?.model) {
    return memory.metadata.supervisor_info.model_info.model;
  }

  if (memory.metadata?.model) {
    return memory.metadata.model;
  }

  if (memory.llmModel) {
    return memory.llmModel;
  }

  return 'Not assigned';
};

const extractExtendedModels = (memory) => {
  if (!memory) return [];

  const models = [];
  const addModelValue = (value) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => addModelValue(v));
      return;
    }
    if (typeof value !== 'string') return;
    value
      .split(',')
      .map((m) => m.trim())
      .filter(Boolean)
      .forEach((m) => models.push(m));
  };

  // Backend stores llm_model/llm_provider in metadata (string or array for comparison) — use as primary source
  const meta = memory.metadata;
  if (meta?.llm_model) addModelValue(meta.llm_model);
  if (meta?.llmModel) addModelValue(meta.llmModel);
  const comparisonResponses = meta?.comparison_responses || meta?.comparisonResponses;
  if (comparisonResponses && Array.isArray(comparisonResponses)) {
    comparisonResponses.forEach((item) => addModelValue(item?.model));
  }

  const modelMap = new Map(); // Use Map to track provider -> model mapping

  // First, check agentsUsed for "Extended-" agents (most reliable source)
  if (memory.agentsUsed && Array.isArray(memory.agentsUsed)) {
    memory.agentsUsed.forEach(agent => {
      if (typeof agent === 'string' && agent.startsWith('Extended-')) {
        const provider = agent.replace('Extended-', '').toUpperCase();
        // Store provider, we'll try to get model from llmResponses
        if (!modelMap.has(provider)) {
          modelMap.set(provider, null);
        }
      }
    });
  }

  // Check llmResponses for extended agents and extract models
  if (memory.llmResponses && Array.isArray(memory.llmResponses)) {
    memory.llmResponses.forEach(response => {
      if (response.agent && typeof response.agent === 'string' && response.agent.startsWith('Extended-')) {
        const provider = response.agent.replace('Extended-', '').toUpperCase();
        // Try to extract model from response metadata or context
        let model = provider; // Default to provider name

        // Check if response has model info
        if (response.model) {
          model = response.model;
        } else if (response.metadata?.model) {
          model = response.metadata.model;
        }

        modelMap.set(provider, model);
      }
    });
  }

  // Add models from the map (agentsUsed + llmResponses)
  modelMap.forEach((model, provider) => {
    const v = model || provider;
    if (v && !models.includes(v)) models.push(v);
  });

  // Remove duplicates and filter out empty values
  const uniqueModels = [...new Set(models.filter(Boolean))];
  return uniqueModels;
};

const getMemorySourceInfo = (memory) => {
  const sourceType = memory?.metadata?.source_type;
  const slackChannel = memory?.metadata?.slack_channel_id;

  if (sourceType === 'slack_mention') {
    return {
      label: 'Slack Mention',
      tone: 'slack',
      detail: slackChannel || null
    };
  }

  if (memory?.metadata?.direct_response) {
    return {
      label: 'Direct Response',
      tone: 'direct',
      detail: null
    };
  }

  if (memory?.metadata?.supervisor_interaction) {
    return {
      label: 'Supervisor',
      tone: 'supervisor',
      detail: null
    };
  }

  return {
    label: 'OmniContext',
    tone: 'default',
    detail: null
  };
};

function MemoryTableView({ memories, onViewDetails, onDelete, isLoading }) {
  const columns = [
    {
      header: 'Timestamp',
      width: '140px',
      render: (memory) => (
        <div className="timestamp-info">
          <div>{new Date(memory.timestamp).toLocaleDateString()}</div>
          <small>{new Date(memory.timestamp).toLocaleTimeString()}</small>
        </div>
      )
    },
    {
      header: 'Query',
      render: (memory) => (
        <div className="query-preview">
          <strong>{(memory.query || memory.userQuery || '').substring(0, 80)}</strong>
          {(memory.query || memory.userQuery || '').length > 80 && '...'}
          {memory.metadata?.extended_query && typeof memory.metadata.extended_query === 'string' && (
            <div className="extended-query">
              <small>Ext: {memory.metadata.extended_query.substring(0, 50)}...</small>
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Source',
      width: '160px',
      render: (memory) => {
        const source = getMemorySourceInfo(memory);
        return (
          <div className="source-cell">
            <span className={`source-badge ${source.tone}`}>{source.label}</span>
            {source.detail && <small>{source.detail}</small>}
          </div>
        );
      }
    },
    {
      header: 'Created By',
      width: '180px',
      render: (memory) => memory.user_email ? (
        <span className="user-email-badge">{memory.user_email}</span>
      ) : (
        <span className="user-email-badge no-email">N/A</span>
      )
    },
    {
      header: 'Supervisor',
      width: '120px',
      render: (memory) => {
        const llmModel = extractLLMModel(memory);
        return <span className="model-badge primary">{llmModel}</span>;
      }
    },
    {
      header: 'Extended Models',
      width: '160px',
      render: (memory) => {
        const extendedModels = extractExtendedModels(memory);
        return extendedModels.length > 0 ? (
          <div className="extended-models-list">
            {extendedModels.slice(0, 2).map((model, idx) => (
              <span key={idx} className="model-badge secondary">{model}</span>
            ))}
            {extendedModels.length > 2 && (
              <span className="model-count">+{extendedModels.length - 2}</span>
            )}
          </div>
        ) : (
          <span className="no-extended">None</span>
        );
      }
    },
    {
      header: 'Actions',
      width: '100px',
      align: 'right',
      render: (memory) => (
        <div className="actions-wrapper">
          <Button
            variant="ghost"
            size="sm"
            icon="fas fa-eye"
            onClick={() => onViewDetails(memory)}
            title="View Details"
          />
          <Button
            variant="ghost"
            size="sm"
            icon="fas fa-trash"
            onClick={() => onDelete(memory)}
            title="Delete"
          />
        </div>
      )
    }
  ];

  return (
    <div className="memory-view active">
      <Table
        columns={columns}
        data={memories}
        isLoading={isLoading}
        emptyMessage="No conversation memories have been stored yet."
        emptyIcon="fas fa-brain"
      />
    </div>
  );
}

function MemoryDetailsModal({ memory, onClose }) {
  if (!memory) return null;

  const llmModel = extractLLMModel(memory);
  const extendedModels = extractExtendedModels(memory);
  const source = getMemorySourceInfo(memory);

  return (
    <PortalModal
      isOpen
      onClose={onClose}
      title={
        <>
          <i className="fas fa-info-circle"></i>
          Memory Entry Details
        </>
      }
      size="large"
      bodyClassName="memory-modal-body"
    >
      <div className="memory-details-content">
        <div className="details-section">
          <h4>Basic Information</h4>
          <div className="details-grid">
            <div className="detail-item">
              <label>ID:</label>
              <span>{memory.memory_id || memory.id || memory._id || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <label>Timestamp:</label>
              <span>{new Date(memory.timestamp).toLocaleString()}</span>
            </div>
            <div className="detail-item">
              <label>Created By:</label>
              {memory.user_email && typeof memory.user_email === 'string' && memory.user_email.trim() !== '' && memory.user_email.includes('@') ? (
                <span className="user-email-badge">
                  {memory.user_email}
                </span>
              ) : (
                <span className="user-email-badge no-email">
                  Not available
                </span>
              )}
            </div>
            <div className="detail-item">
              <label>Source:</label>
              <div className="detail-badge-list">
                <span className={`source-badge ${source.tone}`}>{source.label}</span>
              </div>
            </div>
            {memory.metadata?.slack_channel_id && (
              <div className="detail-item">
                <label>Slack Channel:</label>
                <span>{memory.metadata.slack_channel_id}</span>
              </div>
            )}
            {memory.metadata?.slack_thread_ts && (
              <div className="detail-item">
                <label>Slack Thread:</label>
                <span>{memory.metadata.slack_thread_ts}</span>
              </div>
            )}
            <div className="detail-item">
              <label>Supervisor Model:</label>
              <span>{llmModel}</span>
            </div>
            {memory.confidence && typeof memory.confidence === 'string' && (
              <div className="detail-item">
                <label>Confidence:</label>
                <span className={`confidence-badge ${memory.confidence.toLowerCase()}`}>
                  {memory.confidence}
                </span>
              </div>
            )}
            {extendedModels.length > 0 && (
              <div className="detail-item">
                <label>Extended Models:</label>
                <div className="extended-models-detail">
                  {extendedModels.map((model, index) => (
                    <span key={index} className="model-badge secondary">
                      {model}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="details-section">
          <h4>Query</h4>
          <div className="query-content">
            <p><strong>{memory.query || memory.userQuery || 'No query available'}</strong></p>
            {memory.metadata?.extended_query && typeof memory.metadata.extended_query === 'string' && (
              <div className="extended-query-detail">
                <h5>Extended Query:</h5>
                <p>{memory.metadata.extended_query}</p>
              </div>
            )}
          </div>
        </div>

        {memory.finalAnswer && typeof memory.finalAnswer === 'string' && (
          <div className="details-section">
            <h4>Final Answer</h4>
            <div className="answer-content">
              <MarkdownRenderer
                content={memory.finalAnswer}
                className="memory-full-answer"
              />
            </div>
          </div>
        )}

        {memory.relatedPastQueries && memory.relatedPastQueries.length > 0 && (
          <div className="details-section">
            <h4>Related Context ({memory.relatedPastQueries.length} entries)</h4>
            <div className="context-list">
              {memory.relatedPastQueries.map((query, index) => (
                <div key={index} className="context-item">
                  <span className="context-number">#{index + 1}</span>
                  <span className="context-text">{query}</span>
                </div>
              ))}
            </div>
          </div>
        )}


        {memory.metadata && Object.keys(memory.metadata).length > 0 && (
          <div className="details-section">
            <h4>Additional Metadata</h4>
            <div className="metadata-content">
              <pre>{JSON.stringify(memory.metadata, null, 2)}</pre>
            </div>
          </div>
        )}
      </div>
    </PortalModal>
  );
}

export default function MemoryManagement() {
  const { getMemories, deleteMemory, deleteAllMemories, exportMemories, importMemories, loading } = useMemories();
  const [memories, setMemories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEmail, setFilterEmail] = useState('all');
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState('merge');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState('all');

  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        const response = await apiClient.getWorkspaces();
        setWorkspaces(response?.workspaces || []);
      } catch (error) {
        console.error('Error loading workspaces:', error);
      }
    };
    fetchWorkspaces();
  }, []);

  const loadMemories = useCallback(async () => {
    try {
      const data = await getMemories({}, selectedWorkspace);
      setMemories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading memories:', error);
      setMemories([]);
    }
  }, [getMemories, selectedWorkspace]);

  useEffect(() => {
    loadMemories();
  }, [loadMemories]);

  const handleDeleteMemory = async (memory) => {
    try {
      await deleteMemory(memory.id || memory._id, selectedWorkspace);
      await loadMemories();
    } catch (error) {
      console.error('Error deleting memory:', error);
    }
  };

  const handleDeleteAll = async () => {
    if (memories.length === 0) return;

    const confirmDelete = window.confirm(
      '⚠️ ARE YOU SURE? ⚠️\n\n' +
      'This will permanently delete ALL memory entries and their associated RAG context.\n' +
      'This action cannot be undone.'
    );

    if (confirmDelete) {
      try {
        setDeletingAll(true);
        await deleteAllMemories(selectedWorkspace);
        toastService.success('All memories deleted successfully', 3000);
        await loadMemories();
      } catch (error) {
        console.error('Error deleting all memories:', error);
        toastService.error('Failed to delete all memories', 4000);
      } finally {
        setDeletingAll(false);
      }
    }
  };

  const handleExportMemories = async () => {
    try {
      setExporting(true);
      await exportMemories(null, selectedWorkspace);
      toastService.success('Memories exported successfully!', 3000);
    } catch (error) {
      console.error('Error exporting memories:', error);
      toastService.error('Failed to export memories. Please try again.', 4000);
    } finally {
      setExporting(false);
    }
  };

  const handleImportMemories = async () => {
    if (!importFile) {
      toastService.warning('Please select a file to import', 3000);
      return;
    }

    // Confirm if mode is replace
    if (importMode === 'replace') {
      const confirmed = window.confirm(
        '⚠️ WARNING: This will DELETE ALL existing memories and replace them with the imported data.\n\n' +
        'Are you sure you want to continue?'
      );
      if (!confirmed) {
        return;
      }
    }

    try {
      setImporting(true);
      const result = await importMemories(importFile, importMode, selectedWorkspace);

      const message = `Import completed! Imported: ${result.imported}, Skipped: ${result.skipped}, Total: ${result.total}`;
      toastService.success(message, 5000);

      if (result.errorCount) {
        toastService.warning(`${result.errorCount} errors occurred during import`, 4000);
      }

      // Reload memories
      await loadMemories();

      // Close modal and reset
      setShowImportModal(false);
      setImportFile(null);
      setImportMode('merge');
    } catch (error) {
      console.error('Error importing memories:', error);
      toastService.error(`Failed to import memories: ${error.message}`, 5000);
    } finally {
      setImporting(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.json')) {
        toastService.error('Please select a JSON file', 3000);
        event.target.value = '';
        return;
      }
      setImportFile(file);
    }
  };

  // Get unique user emails from memories for the filter dropdown
  const uniqueEmails = useMemo(() => {
    const emails = memories
      .map(memory => memory.user_email || memory.userEmail)
      .filter(email => email && email.trim() !== '')
      .filter((email, index, self) => self.indexOf(email) === index)
      .sort();
    return emails;
  }, [memories]);

  const filteredMemories = memories.filter(memory => {
    const extendedQuery = memory.metadata?.extended_query;
    const extendedQueryStr = typeof extendedQuery === 'string' ? extendedQuery : '';

    const matchesSearch = !searchTerm ||
      (memory.query || memory.userQuery || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      extendedQueryStr.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesEmail = filterEmail === 'all' ||
      (memory.user_email || memory.userEmail || '').toLowerCase() === filterEmail.toLowerCase();

    return matchesSearch && matchesEmail;
  });

  // Pagination calculations
  const totalItems = filteredMemories.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMemories = filteredMemories.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterEmail, itemsPerPage, selectedWorkspace]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="page-container memory-management">
      <div className="page-content">
        <PageHeader
          title="Memory Management"
          description="View and manage conversation memories stored by the system"
          icon="fas fa-brain"
          accentColor="purple"
        />

        <div className="section-content">
        <div className="mm-toolbar">
          <div className="mm-filters">
            <div className="search-wrapper">
              <SearchBar
                placeholder="Search memories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <Select
              value={selectedWorkspace}
              onChange={(e) => setSelectedWorkspace(e.target.value)}
              title="Filter by Workspace"
              options={[
                { value: 'all', label: 'All Memories (Everything)' },
                { value: 'company', label: 'Company Context' },
                ...workspaces.map(ws => ({ value: ws._id || ws.id, label: ws.name }))
              ]}
            />

            <Select
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              title="Filter by user email"
              options={[
                { value: 'all', label: 'All Users' },
                ...uniqueEmails.map(email => ({ value: email, label: email }))
              ]}
            />
          </div>

          <div className="mm-toolbar-actions">
            <Button
              variant="secondary"
              icon={exporting ? "fas fa-spinner fa-spin" : "fas fa-download"}
              onClick={handleExportMemories}
              disabled={exporting || loading}
              title="Export all memories to JSON"
            >
              Export
            </Button>
            <Button
              variant="secondary"
              icon="fas fa-upload"
              onClick={() => setShowImportModal(true)}
              disabled={importing || loading}
              title="Import memories from JSON"
            >
              Import
            </Button>
            <Button
              variant="danger"
              icon={deletingAll ? "fas fa-spinner fa-spin" : "fas fa-trash-alt"}
              onClick={handleDeleteAll}
              disabled={deletingAll || loading || memories.length === 0}
              title="Delete ALL memories"
            >
              Delete All
            </Button>
            <Button
              variant="primary"
              icon="fas fa-sync-alt"
              onClick={loadMemories}
            >
              Refresh
            </Button>
          </div>
        </div>

        <div className="memory-content">
            <>
              <MemoryTableView
                memories={paginatedMemories}
                onViewDetails={setSelectedMemory}
                onDelete={handleDeleteMemory}
                isLoading={loading}
              />

              {/* Pagination Controls */}
              {totalItems > 0 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    <span>
                      Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                    </span>

                  </div>

                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      title="First page"
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      title="Previous page"
                    >
                      <i className="fas fa-angle-left"></i>
                    </button>

                    <div className="pagination-pages">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNumber;
                        if (totalPages <= 5) {
                          pageNumber = i + 1;
                        } else if (currentPage <= 3) {
                          pageNumber = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i;
                        } else {
                          pageNumber = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNumber}
                            className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                            onClick={() => handlePageChange(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      title="Next page"
                    >
                      <i className="fas fa-angle-right"></i>
                    </button>
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      title="Last page"
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </button>
                  </div>

                  <div className="items-per-page">
                    <span>Show:</span>
                    <Select
                      className="pagination-select"
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                      options={[
                        { value: 10, label: '10' },
                        { value: 25, label: '25' },
                        { value: 50, label: '50' },
                        { value: 100, label: '100' }
                      ]}
                    />
                  </div>
                </div>
              )}
            </>
        </div>
      </div>

      {/* Memory Details Modal */}
      {selectedMemory && (
        <MemoryDetailsModal
          memory={selectedMemory}
          onClose={() => setSelectedMemory(null)}
        />
      )}

      {/* Import Modal */}
      <PortalModal
        isOpen={showImportModal}
        onClose={() => !importing && setShowImportModal(false)}
        title={
          <span>
            <i className="fas fa-upload"></i>
            Import Memories
          </span>
        }
        size="small"
        bodyClassName="memory-modal-body"
        footer={
          <div className="import-actions">
            <Button
              variant="secondary"
              onClick={() => setShowImportModal(false)}
              disabled={importing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={importing ? 'fas fa-spinner fa-spin' : 'fas fa-upload'}
              onClick={handleImportMemories}
              disabled={!importFile || importing}
            >
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </div>
        }
      >
        <div className="import-modal-content">
          <div className="import-section">
            <h4>Select Import Mode</h4>
            <div className="import-mode-options">
              <label className="radio-option">
                <input
                  type="radio"
                  name="importMode"
                  value="merge"
                  checked={importMode === 'merge'}
                  onChange={(e) => setImportMode(e.target.value)}
                  disabled={importing}
                />
                <div className="radio-label">
                  <strong>Merge</strong>
                  <p>Add imported memories to existing ones (skip duplicates)</p>
                </div>
              </label>

              <label className="radio-option">
                <input
                  type="radio"
                  name="importMode"
                  value="replace"
                  checked={importMode === 'replace'}
                  onChange={(e) => setImportMode(e.target.value)}
                  disabled={importing}
                />
                <div className="radio-label">
                  <strong>Replace</strong>
                  <p className="warning-text">⚠️ Delete all existing memories and import new ones</p>
                </div>
              </label>
            </div>

          </div>

          <div className="import-section">
            <h4>Select JSON File</h4>
            <div className="file-input-container">
              <input
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                disabled={importing}
                className="file-input"
                id="import-file-input"
              />
              <label htmlFor="import-file-input" className="file-input-label">
                <i className="fas fa-file-upload"></i>
                {importFile ? importFile.name : 'Choose JSON file...'}
              </label>
            </div>
            {importFile && (
              <div className="file-info">
                <i className="fas fa-check-circle"></i>
                File selected: {importFile.name} ({(importFile.size / 1024).toFixed(2)} KB)
              </div>
            )}
          </div>
        </div>
      </PortalModal>
      </div>
    </div>
  );
}
